// Подсказки «Как пользоваться» для каждой страницы кабинета.
//
// Правила (решения 22.10, тред «Онбординг и обучение в кабинете»):
// - подсказки страниц открываются только по кнопке «Помощь › Как
//   пользоваться» или «?», сами не появляются;
// - короткий обзор меню один раз при первом входе показывает само меню
//   (navV3/NavV3Tour.jsx), не этот файл;
// - на странице не больше MAX_STEPS шагов, и каждый говорит, что здесь
//   сделать, а не какая кнопка что делает;
// - слова как в новом меню (menuV3.config.js) и без «SKU», «юнит», «AI».
//
// Шаг без selector показывается окном по центру. Шаг с selector
// показывается, только если элемент есть на экране, иначе пропускается.
// Файл без React и antd, чтобы его можно было проверить тестом.

export const MAX_STEPS = 5;

const step = (selector, title, description, placement = "bottom", text) => ({
  selector,
  text,
  title,
  description,
  placement,
});

const intro = (title, description) => ({ title, description });

const MARKETPLACE_STEP = step(
  '[data-tour-id="top-marketplace"]',
  "Wildberries или Ozon",
  "Выберите, какой магазин смотреть. Страница останется та же.",
  "bottomLeft"
);

// Селекторы прежней страницы (.pnl-…) и новой (data-tour-id="pnl-…", page/profit): какая
// из них открыта, решает PageV3Switch.
const PROFIT_REPORT_STEPS = [
  step(
    '[data-tour-id="pnl-summary"], .pnl-kpis',
    "Главные цифры",
    "Сколько денег пришло, сколько ушло на комиссию, доставку и налоги и сколько осталось вам.",
    "bottom"
  ),
  step(
    '[data-tour-id="pnl-period"], .pnl-header-actions .ant-picker',
    "Выберите месяцы",
    "Отчёт считается за выбранные месяцы. Текущий месяц предварительный: цифры ещё могут измениться.",
    "bottomLeft"
  ),
  step(
    '[data-tour-id="pnl-table"], .pnl-table-scroll',
    "По месяцам и статьям",
    "Каждая строка — доход или расход. Расходы со знаком минус. Прочерк значит, что данных пока нет.",
    "top"
  ),
  step(
    '[data-tour-id="pnl-checks"]',
    "Если прибыль не посчитана",
    "Здесь написано, чего не хватает — закупочных цен, налога или отчётов маркетплейса, — и кнопка, чтобы это исправить.",
    "top"
  ),
  step(
    '[data-tour-id="pnl-download"], .pnl-header-actions button',
    "Скачать для бухгалтера",
    "Отчёт можно сохранить в Excel или PDF.",
    "bottomRight",
    "Скачать"
  ),
];

const PROFIT_ITEMS_STEPS = (mp) => [
  step(
    `[data-tour-id="${mp}-unit-summary"]`,
    "Итог за период",
    "Сколько принесли товары после всех расходов и что мешает посчитать точно.",
    "bottom"
  ),
  step(
    `[data-tour-id="${mp}-unit-period"]`,
    "Выберите даты",
    "Цифры пересчитаются за выбранные дни.",
    "bottomLeft"
  ),
  step(
    `[data-tour-id="${mp}-unit-table"]`,
    "Каждый товар отдельно",
    "Строка — товар: сколько он приносит со всех продаж и с одной штуки. Нажмите на товар, чтобы увидеть, из чего сложилась прибыль.",
    "top"
  ),
  step(
    `[data-tour-id="${mp}-unit-cost"]`,
    "Закупочная цена",
    "Без неё прибыль по товару не посчитать. Впишите её в подробностях товара или в «Закупочных ценах».",
    "bottom"
  ),
  step(
    `[data-tour-id="${mp}-unit-export"]`,
    "Скачать таблицу",
    "Все товары в Excel.",
    "bottomRight"
  ),
];

const TIPS_STEPS = (mp) => [
  intro(
    "Советы",
    "Кабинет смотрит на продажи, остатки, цены и рекламу и подсказывает, что сделать, чтобы продавать больше."
  ),
  step(
    `[data-tour-id="ai-${mp}-column-today"]`,
    "Сделать сегодня",
    "Самое срочное: то, что уже теряет деньги. Начните отсюда.",
    "right"
  ),
  step(
    `[data-tour-id="ai-${mp}-card"]`,
    "Один совет",
    "Что случилось, что сделать и чего ждать после.",
    "right"
  ),
  step(
    `[data-tour-id="ai-${mp}-card-menu"]`,
    "Отметьте, когда сделали",
    "Сделанный совет уйдёт из списка, а кабинет посмотрит, что изменилось.",
    "left"
  ),
];

// Селекторы новой страницы (page/goods, data-tour-id="orders-…") и прежней таблицы: какая
// из них открыта, решает PageV3Switch.
const ORDERS_STEPS = (mp, daysColumn) => [
  step(
    '[data-tour-id="orders-attention"]',
    "Что с товарами",
    "Какие товары скоро закончатся, уже закончились или их стали заказывать меньше.",
    "bottom"
  ),
  step(
    `[data-tour-id="${mp}-orders-scroll"]`,
    "Заказы и остатки",
    "Строка — товар: сколько заказывают по неделям и сколько лежит на складах. Зелёный — заказывают больше, чем неделей раньше, красный — меньше.",
    "top"
  ),
  step(
    `[data-tour-id="orders-days"], [data-tour-id="${mp}-orders-column-${daysColumn}"]`,
    "На сколько дней хватит",
    "Если дней мало, пора везти новую поставку.",
    "bottom"
  ),
  step(
    'input[aria-label="Найти товар"], input[placeholder*="Поиск"]',
    "Найти товар",
    "Начните вводить название или артикул.",
    "bottom"
  ),
  step(
    "button",
    "Скачать таблицу",
    "Вся таблица в Excel.",
    "bottomRight",
    "Excel"
  ),
];

const PURCHASE_PRICES_STEPS = (mp) => [
  intro(
    "Закупочные цены",
    "Укажите, почём вы покупаете каждый товар. Без этого кабинет не посчитает прибыль точно."
  ),
  step(
    `[data-tour-id="${mp}-costs-page"] button`,
    "Скачать список товаров",
    "Скачайте файл, впишите цены в Excel…",
    "bottom",
    "Скачать"
  ),
  step(
    `[data-tour-id="${mp}-costs-page"] button`,
    "…и загрузите обратно",
    "Цены появятся у всех товаров сразу.",
    "bottom",
    "Загрузить"
  ),
  step(
    `[data-tour-id="${mp}-costs-editor"]`,
    "Или по одному",
    "Цену можно вписать прямо в строке товара.",
    "top"
  ),
];

const TOURS = [
  {
    id: "home",
    match: (pathname) => pathname === "/",
    steps: [
      intro(
        "Главная",
        "Здесь видно, что уже настроено и что сделать дальше. Нажмите кнопку у ближайшего шага — дальше кабинет подскажет сам."
      ),
      MARKETPLACE_STEP,
      step(
        '[data-tour-id="top-company-selector"]',
        "Ваш магазин",
        "Если магазинов несколько, переключайтесь здесь.",
        "bottomRight"
      ),
      step(
        '[data-tour-id="sidebar-support"]',
        "Помощь",
        "Здесь короткие ответы на вопросы, подсказки по странице и связь с поддержкой.",
        "right"
      ),
    ],
  },
  {
    id: "wb-profit-report",
    match: (pathname) => pathname.startsWith("/wildberries/pnl"),
    ready: '[data-tour-id="pnl-page"], .pnl-header',
    steps: PROFIT_REPORT_STEPS,
  },
  {
    id: "ozon-profit-report",
    match: (pathname) => pathname.startsWith("/ozon/pnl"),
    ready: '[data-tour-id="pnl-page"], .pnl-header',
    steps: PROFIT_REPORT_STEPS,
  },
  {
    id: "wb-profit-items",
    match: (pathname) => pathname.startsWith("/wildberries/wbUnit"),
    ready: '[data-tour-id="wb-unit-table"]',
    steps: PROFIT_ITEMS_STEPS("wb"),
  },
  {
    id: "ozon-profit-items",
    match: (pathname) => pathname.startsWith("/ozon/ozonUnit"),
    ready: '[data-tour-id="ozon-unit-table"]',
    steps: PROFIT_ITEMS_STEPS("ozon"),
  },
  {
    id: "wb-tips",
    match: (pathname) => pathname.startsWith("/wildberries/wbAI"),
    ready: '[data-tour-id="ai-wb-board"]',
    steps: TIPS_STEPS("wb"),
  },
  {
    id: "ozon-tips",
    match: (pathname) => pathname.startsWith("/ozon/ozonAI"),
    ready: '[data-tour-id="ai-ozon-board"]',
    steps: TIPS_STEPS("ozon"),
  },
  {
    id: "wb-orders",
    match: (pathname) => pathname.startsWith("/wildberries/wbDashboard"),
    ready: '[data-tour-id="wb-orders-scroll"], [data-tour-id="wb-orders-table"]',
    steps: ORDERS_STEPS("wb", "idc"),
  },
  {
    id: "ozon-orders",
    match: (pathname) => pathname.startsWith("/ozon/ozonDashboard"),
    ready: '[data-tour-id="ozon-orders-scroll"], [data-tour-id="ozon-orders-table"]',
    steps: ORDERS_STEPS("ozon", "idcFbo"),
  },
  {
    id: "wb-purchase-prices",
    match: (pathname) => pathname.startsWith("/wildberries/costs"),
    steps: PURCHASE_PRICES_STEPS("wb"),
  },
  {
    id: "ozon-purchase-prices",
    match: (pathname) => pathname.startsWith("/ozon/costs"),
    steps: PURCHASE_PRICES_STEPS("ozon"),
  },
  {
    id: "keys-ozon",
    match: (pathname, search = "") =>
      pathname.startsWith("/changeApi") &&
      new URLSearchParams(search).get("marketplace") === "ozon",
    ready: '[data-tour-id="api-page"]',
    steps: [
      step(
        '[data-tour-id="api-tabs"]',
        "Подключения магазина",
        "Здесь видно, что работает. Нажмите кнопку рядом с Ozon или рекламой Ozon, чтобы подключить или заменить ключ.",
        "bottom"
      ),
      step(
        '[data-tour-id="api-guide-ozon-main"]',
        "Где взять ключ",
        "Пошаговая инструкция: что нажать в кабинете Ozon, чтобы получить ключ.",
        "bottom"
      ),
      step(
        '[data-tour-id="api-ozon-main-client"]',
        "Client ID",
        "Номер из кабинета Ozon, он стоит рядом с ключом.",
        "bottom"
      ),
      step(
        '[data-tour-id="api-ozon-main-key"]',
        "Ключ",
        "Длинная строка из кабинета Ozon. Скопируйте и вставьте целиком.",
        "bottom"
      ),
      step(
        '[data-tour-id="api-ozon-main-save"]',
        "Подключить",
        "Кабинет проверит ключ и начнёт загружать продажи. Страницу можно закрыть.",
        "top"
      ),
    ],
  },
  {
    id: "keys-wb",
    match: (pathname, search = "") =>
      pathname.startsWith("/changeApi") &&
      new URLSearchParams(search).get("marketplace") !== "ozon",
    ready: '[data-tour-id="api-page"]',
    steps: [
      step(
        '[data-tour-id="api-tabs"]',
        "Подключения магазина",
        "Здесь видно, что работает. Нажмите кнопку рядом с Wildberries, чтобы подключить или заменить ключ.",
        "bottom"
      ),
      step(
        '[data-tour-id="api-guide-wb"]',
        "Где взять ключ",
        "Пошаговая инструкция: что нажать в кабинете Wildberries и какие галочки поставить.",
        "bottom"
      ),
      step(
        '[data-tour-id="api-wb-token"]',
        "Вставьте ключ",
        "Скопируйте ключ из кабинета Wildberries и вставьте сюда целиком.",
        "bottom"
      ),
      step(
        '[data-tour-id="api-wb-save"]',
        "Сохранить",
        "Кабинет проверит ключ и начнёт загружать продажи. Страницу можно закрыть.",
        "top"
      ),
    ],
  },
  {
    id: "task-list",
    match: (pathname) => pathname === "/task" || pathname === "/task/",
    ready: '[data-tour-id="task-list-table"]',
    steps: [
      step(
        '[data-tour-id="task-list-stats"]',
        "Сколько задач",
        "Сколько задач в работе, сколько просрочено и сколько сделано.",
        "bottom"
      ),
      step(
        '[data-tour-id="task-list-table"] button',
        "Новая задача",
        "Поручите дело себе или сотруднику и поставьте срок.",
        "bottom",
        "Создать"
      ),
      step(
        '[data-tour-id="task-list-table"] .task-title-link',
        "Открыть задачу",
        "Нажмите на название, чтобы посмотреть подробности и написать комментарий.",
        "right"
      ),
    ],
  },
  {
    id: "task-create",
    match: (pathname) => pathname.startsWith("/creattask"),
    ready: '[data-tour-id="task-create-form"]',
    steps: [
      step('[data-tour-id="task-create-title"]', "Что сделать", "Коротко, одной строкой.", "bottom"),
      step('[data-tour-id="task-create-people"]', "Кто делает", "Выберите сотрудника или себя.", "bottom"),
      step('[data-tour-id="task-create-dates"]', "К какому сроку", "Кабинет напомнит, когда срок подойдёт.", "bottom"),
      step('[data-tour-id="task-create-save"]', "Создать", "Сотрудник получит уведомление.", "top"),
    ],
  },
  {
    id: "task-detail",
    match: (pathname) => pathname.startsWith("/getCurrentTask/"),
    ready: '[data-tour-id="task-detail-form"]',
    steps: [
      step('[data-tour-id="task-detail-status-priority"]', "Как идут дела", "Меняйте статус, когда задача сдвинулась.", "bottom"),
      step('[data-tour-id="task-detail-comments"]', "Обсуждение", "Пишите здесь, чтобы всё по задаче было в одном месте.", "top"),
      step('[data-tour-id="task-detail-save"]', "Сохранить", "Не забудьте сохранить изменения.", "top"),
    ],
  },
  {
    id: "task-child",
    match: (pathname) => pathname.startsWith("/editChild/"),
    ready: '[data-tour-id="task-child-form"]',
    steps: [
      step('[data-tour-id="task-child-parent"]', "Часть большой задачи", "Здесь видно, к какой задаче относится эта.", "bottom"),
      step('[data-tour-id="task-child-comments"]', "Обсуждение", "Пишите здесь, чтобы всё по задаче было в одном месте.", "top"),
      step('[data-tour-id="task-child-save"]', "Сохранить", "Не забудьте сохранить изменения.", "top"),
    ],
  },
  {
    id: "staff-access",
    match: (pathname) => pathname.startsWith("/settings/AddNewUser"),
    steps: [
      step('[data-tour-id="employees-add"]', "Добавить сотрудника", "Пригласите человека и выберите, что ему можно смотреть.", "bottom"),
      step('[data-tour-id="employees-table"]', "Кто имеет доступ", "Все, кто может зайти в ваш кабинет. Доступ можно изменить или забрать.", "top"),
    ],
  },
  {
    id: "staff-teams",
    match: (pathname) => pathname.startsWith("/settings/commands"),
    steps: [
      step('[data-tour-id="teams-form"]', "Группа сотрудников", "Соберите сотрудников в группу, чтобы давать задачи сразу всем.", "bottom"),
      step('[data-tour-id="teams-list"]', "Ваши группы", "Здесь их можно изменить или удалить.", "top"),
    ],
  },
  {
    id: "friend",
    match: (pathname) => pathname.startsWith("/referral"),
    steps: [
      step('[data-tour-id="referral-link"]', "Ваша ссылка", "Отправьте её знакомому селлеру.", "bottom"),
      step('[data-tour-id="referral-copy"]', "Скопировать", "Скопируйте ссылку одним нажатием.", "bottom"),
      step('[data-tour-id="referral-conditions"]', "Сколько вы получите", "Когда знакомый оплатит подписку, вам начислят деньги.", "top"),
      step('[data-tour-id="referral-payout"]', "Куда перевести", "Укажите реквизиты, чтобы получить выплату.", "top"),
    ],
  },
  {
    id: "chat",
    match: (pathname) => pathname.startsWith("/chat"),
    steps: [intro("Чат", "Переписка с сотрудниками вашего магазина.")],
  },
  {
    id: "video-call",
    match: (pathname) => pathname.startsWith("/videCall"),
    steps: [intro("Видеозвонок", "Созвон с сотрудниками прямо из кабинета, без других программ.")],
  },
  {
    id: "phone-login",
    match: (pathname) => pathname.startsWith("/application"),
    steps: [intro("Вход с телефона", "Как открыть кабинет на телефоне.")],
  },
  {
    id: "payment",
    match: (pathname) => pathname.startsWith("/payment"),
    steps: [intro("Оплата", "Сколько дней подписки осталось и как её продлить.")],
  },
  {
    id: "profile",
    match: (pathname) => pathname.startsWith("/mydata"),
    steps: [intro("Профиль и почта", "Имя, телефон и почта. На почту приходят чеки об оплате.")],
  },
  {
    id: "add-shop",
    match: (pathname) =>
      pathname.startsWith("/sellertnewuserpage") || pathname.startsWith("/addnewcompany"),
    steps: [
      intro(
        "Новый магазин",
        "Для начала хватит названия. Реквизиты нужны только для документов, их можно заполнить позже."
      ),
    ],
  },
];

export const FALLBACK_TOUR = Object.freeze({
  id: "fallback",
  steps: [
    intro(
      "Подсказок пока нет",
      "Для этой страницы мы ещё не написали подсказки. Если что-то непонятно, напишите в поддержку через «Помощь»."
    ),
  ],
});

export function getTourForPath(pathname = "", search = "") {
  return TOURS.find((tour) => tour.match(pathname, search)) || FALLBACK_TOUR;
}

export const ALL_TOURS = TOURS;
